import { Collection, IModel, IViolation } from '../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { DataFlowStream, Transformation } from 'app/model/dataflow/transformation';
import { DataFlowSink, FileNameOption } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';

import { Named, Owned } from 'app/model/base/named';
import { sanitizeAndFormatString } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { LinkedService } from 'app/model/linkedservice/linkedservice';
import {
    ActionConstants,
    AdapterContext,
    AzureSqlDWTableDataset,
    Copier,
    Folder,
    getGeneralNamePatterns,
    IOwnedViolation,
    IParameterizable,
    ModelConstants,
    OwnedSet,
    Shadow,
    generateIdentity,
    setIdentity,
    IValidationContext,
    IResourceContext
} from 'shell-models-lib';
import { RequiredValidator } from 'app/model/base/validators';
import { Parameter } from '../base/parameter';
import { DataFlowParameter } from './dataflow-parameter';
import { DataFlowFunction } from './dataflow-function';
import {
    getDataFlowBaseArguments,
    getExpressionReturnTypeAndUpdateIdentifiers
} from 'app/shared/components/parameterizable/parameterizable-dataflow-utilities';
import {
    IDataFlow,
    IDataFlowMappingTypeProperties,
    IDataFlowPreviewProperties,
    IDataFlowProperties
} from '../adapters/dataflow/IDataflow';
import { DataFlowParser, ParseScriptMethod } from '../adapters/dataflow/dataflow.parser';
import { GlobalVariables } from 'app/services/globals';
import { ParameterValue } from '../base/parameter-value';
import { compact, Dictionary } from 'lodash';
import { Application } from 'app/application';
import { LoggerService } from 'app/logger/logger.service';
import { PerformanceEvent } from 'app/logger/logger.performance-event';
import { loggerConstants } from 'app/logger/logger.constants';
import { MessagingService, MessagingServiceChannel } from 'app/services/messaging/messaging-service';
import { Message, MessageKind } from 'app/services/messaging/message';
import { of } from 'rxjs';
import { DataFlowDataPlaneService } from 'app/apis/data-flow-dataplane-api.service';
import { DataFlowExpression } from './dataflowexpression';
import { Group } from './group';
import { WranglingDataflow } from './wranglingDataflow';
import { DataFlowUtils } from 'app/authoring/component/transformations/dataFlow-utils';
import { CallTransformation, ICallProperties } from './calltransformation';
import { assignDataFlowExpressionValue, isTruthy } from './dataflow-expression-utilities';
import { SapOdpDataset } from '../dataset/sap-odp.dataset';
import { SynapseAnalyticsSettings } from './dataset-settings/synapse-analytics-settings';
import { HiveSettings } from './dataset-settings/hive-dataset-settings';
import { SapOdpSettings } from './dataset-settings/sap-odp-settings';
import { DataFlowReference } from './dataflow-reference';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { StringDataFlowType } from './dataflow-datatype';
import { isSapCDCEnabled, isSapODataSource, isSapOdpSource } from 'app/model/dataflow/dataflow-utils';
import { ExecuteFlowletTransformation } from 'app/model/dataflow/executeflowlettransformation';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';

export interface ISinkGroup {
    groupName: string;
    groupId: number;
    saveOrder?: number;
    sinks: Transformation[];
}

// plz do not inherit IDataFlowModelParserOption from Product/Source/app/model/adapters/dataflow/dataflow.parser.ts
// do not couple more things together, let ts does the type check itself
export interface IDataFlowModelParseDslAsyncOption {
    method: ParseScriptMethod;
    parserVersion: 'vStable' | 'v1' | 'v2';
}

@Metamodel.model({
    name: ModelConstants.dataFlow,
    display: [stringResources.DataFlow, stringResources.DataFlows],
    reusable: true,
    referable: true,
    constraints: {
        name: {
            required: true,
            // MDF data flow names are Fabric artifact display names governed by Fabric's own
            // naming rules (which allow parentheses, longer names, etc.). Applying ADF's stricter
            // patterns here produces false, un-fixable errors, so skip them for MDF.
            patterns: __IS_FABRIC_MDF__ ? [] : getGeneralNamePatterns(stringResources.DataFlow),
            uniqueModelName: true
        }
    },
    validator: DataFlow.prototype.validate,
    tags: { gitValidator: DataFlow.prototype.gitValidate },
    accessPublishConstraint: {
        writeResource: {
            action: ActionConstants.dataFlowsWrite
        },
        deleteResource: {
            action: ActionConstants.dataFlowsDelete
        }
    }
})
export class DataFlow extends Named implements IParameterizable {
    public static readonly MODEL: IModel;

    // Adding here to avoid circular dependency with execute flowlet -> flowlet -> data flow parser
    private static _EXECUTE_FLOWLET_MODEL_NAME = 'compose';
    private static _CALL_TRANSFORM_MODEL_NAME = 'call';

    @Metamodel.attribute({
        transient: true
    })
    public lastDebugJobVersion: string;

    @Metamodel.attribute({
        transient: true
    })
    public forceToGetNewJobVersion: boolean = true;

    @Metamodel.attribute({
        transient: true
    })
    public fetchFromSourcesAgain: boolean = true;

    @Metamodel.attribute({
        transient: true
    })
    public changesMadeSinceLastJob: boolean = false;

    @Metamodel.attribute({
        transient: true
    })
    public containsUnknownDSL: boolean = false;

    @Metamodel.attribute({
        type: Transformation.MODEL,
        collection: Collection.Set
    })
    public readonly transformations = OwnedSet.create<Transformation>(this);

    @Metamodel.attribute({
        type: DataFlowSource.MODEL,
        collection: Collection.Set
    })
    public sources = OwnedSet.create<DataFlowSource>(this);

    @Metamodel.attribute({
        type: Transformation.MODEL,
        collection: Collection.Set
    })
    public sourceFlowlets = OwnedSet.create<Transformation>(this);

    @Metamodel.attribute({
        type: DataFlowSink.MODEL,
        collection: Collection.Set,
        constraint: {
            validator: DataFlow.validateSinks
        }
    })
    public sinks = OwnedSet.create<DataFlowSink>(this);

    @Metamodel.attribute({
        type: DataFlowReference.MODEL,
        reference: true,
        collection: Collection.Set
    })
    public udfLibraries = new Set<DataFlow>();

    @Metamodel.attribute({
        type: Group.MODEL,
        collection: Collection.Set
    })
    public groups = OwnedSet.create<Group>(this);

    @Metamodel.attribute({
        display: stringResources.LinkedService,
        reference: true,
        type: LinkedService.MODEL,
        constraint: {
            validator: DataFlow.validateDependencies
        }
    })
    public dependenciesLinkedService: LinkedService;

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true,
            validator: DataFlow.validateParameters
        }
    })
    public readonly parameters = OwnedSet.create<DataFlowParameter>(this);

    @Metamodel.attribute({
        type: Parameter.MODEL,
        collection: Collection.Set,
        constraint: {
            unique: true
        }
    })
    public readonly functions = OwnedSet.create<DataFlowFunction>(this);

    public dependenciesPaths: string[] = [];

    public folder: Folder = Owned.create(Folder.MODEL, this);

    public cacheUpdated: boolean = false;

    public parametersMissingValues: boolean = false;

    public maxSinkOrder: number = 0;

    public sinkGroups: ISinkGroup[] = [];

    public sqlErrorContainer?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public sqlErrorFileSystem?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public sqlErrorFolderPath?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public sqlErrorStoragePath?: String;

    public assertFailureContainer?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public assertFailureFileSystem?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public assertFailureFolderPath?: DataFlowExpression = this.createTemplatableExpression(StringDataFlowType.TYPE);

    public assertFailureStoragePath?: String;

    public showDataPreview: boolean = true;

    public savedWithSingleScript: boolean = false;

    public get cacheSinks(): OwnedSet<DataFlowSink> {
        return this.sinks.filter((sink) => sink.isCacheSink);
    }

    /**
     * True when any source in this data flow references a SAP CDC / SAP ODP linked service.
     * Used by the resource explorer sidebar and pipeline banner to surface a deprecation warning
     * for pipelines that execute a data flow whose sources use SAP ODP / SAP CDC.
     */
    public get hasSapCdcDependency(): boolean {
        const sapCdcLsTypes = [
            adfModelTypeMap.sapOdp.linkedServiceType,
            adfModelTypeMap.sapCdc.linkedServiceType
        ];
        for (const source of this.sources) {
            const ls = source.sourceLinkedService || source.sourceDataset?.linkedService;
            if (ls && sapCdcLsTypes.includes(Metamodel.modelOf(ls).name)) {
                return true;
            }
        }
        return false;
    }

    public hasParsed: boolean = false;

    protected _lastStoredResource: IDataFlowMappingTypeProperties;

    constructor(name?: string, description?: string) {
        super(name, description);
    }

    public static validateDependencies(
        dataflow: DataFlow,
        value?: LinkedService,
        violation?: IViolation,
        context?: IValidationContext
    ): IViolation[] | undefined {
        const violations: IViolation[] = [];
        if (dataflow.dependenciesPaths.length && !dataflow.dependenciesLinkedService) {
            return RequiredValidator.INSTANCE.validate(
                {},
                DataFlow.MODEL.attribute('dependenciesLinkedService'),
                [dataflow.dependenciesLinkedService],
                dataflow,
                context
            );
        }

        return violations;
    }

    public static validateParameters(dataflow: DataFlow | WranglingDataflow): IViolation[] | undefined {
        const violations: IViolation[] = [];
        dataflow.parameters.forEach((parameter) => {
            if (parameter.name.includes('@') || parameter.name.includes(' ') || parameter.name.includes('.')) {
                violations.push({
                    context: parameter,
                    message: () => sanitizeAndFormatString(stringResources.DataFlowInvalidParameterName, parameter.name)
                });
            }
            if (parameter.defaultValue.includes(`$${parameter.name}`)) {
                violations.push({
                    context: parameter,
                    message: () =>
                        sanitizeAndFormatString(
                            stringResources.DataFlowInvalidParameterValueSelfReferenced,
                            parameter.name
                        )
                });
            }
        });

        return violations;
    }

    public static validateSinks(dataFlow: DataFlow): IViolation[] {
        const violations: IViolation[] = [];
        let cacheSinksWithOutput = 0;
        dataFlow.sinks.forEach((sink) => {
            if (sink.fileNameOption === FileNameOption.SingleFile && sink.partitionOption !== 'NoPartition') {
                violations.push({
                    context: sink,
                    message: () => stringResources.DataFlowErrorSingleFile_SinkPartitioning
                });
            }
            if (sink.cacheSinkOutput.expression === 'true') {
                cacheSinksWithOutput += 1;
            }
        });
        if (cacheSinksWithOutput > 1) {
            violations.push({
                context: dataFlow,
                message: () => stringResources.DataFlowErrorMultipleCacheSinkOutput
            });
        }
        return violations;
    }

    public clear(): void {
        super.clear();
        this.sources.clear();
        this.sinks.clear();
        this.udfLibraries.clear();
        this.transformations.clear();
        this.parameters.clear();
    }

    public addParameters(parameters: Set<DataFlowParameter>): DataFlow {
        parameters.forEach((param) => this.parameters.add(param));
        return this;
    }

    public addFunctions(functions: Set<DataFlowFunction>): DataFlow {
        functions.forEach((func) => this.functions.add(func));
        return this;
    }

    public addTransformation(transformation: Transformation): DataFlow {
        this.transformations.add(transformation);
        return this;
    }

    public addSource(source: DataFlowSource): DataFlow {
        this.sources.add(source);
        return this;
    }

    public addSourceFlowlet(flowlet: Transformation): DataFlow {
        this.sourceFlowlets.add(flowlet);
        return this;
    }

    public addSink(sink: DataFlowSink): DataFlow {
        this.sinks.add(sink);
        return this;
    }

    public addUdfLibrary(newLib: DataFlow): DataFlow {
        if (!Array.from(this.udfLibraries.keys()).find((lib) => lib.name === newLib.name)) {
            this.udfLibraries.add(newLib);
        }
        return this;
    }

    public addGroup(group: Group): DataFlow {
        this.groups.add(group);
        return this;
    }

    public getContainingGroup(transformationName: string): Group {
        if (!GlobalVariables.enableGroup) {
            return undefined;
        }
        return this.groups.find((group: Group) => group.transformations.has(transformationName));
    }

    public uniqueName(transformationType: IModel, prefix?: string): string {
        if (!prefix) {
            prefix = transformationType.name;
        }
        const regex = new RegExp('^' + prefix + '[0-9]+$');

        let maxNumber = 0;

        const transformations = transformationType === Group.MODEL ? this.groups : this.getAllTransformations();
        if (transformationType !== Group.MODEL) {
            const transformationsArray = transformations.filter(
                (tx) => tx instanceof CallTransformation
            ) as CallTransformation[];
            if (transformationsArray) {
                const callTxExtraTransforms: ICallProperties[] = transformationsArray.map<ICallProperties>((tx) => ({
                    source: tx.source,
                    selectTx: tx.selectTx
                }));
                for (const extraTransforms of callTxExtraTransforms) {
                    if (extraTransforms.source && extraTransforms.selectTx) {
                        (transformations as Transformation[]).push(extraTransforms.source);
                        (transformations as Transformation[]).push(extraTransforms.selectTx);
                    }
                }
            }
        }

        for (const transformation of transformations) {
            if (transformation.name && transformation.name.match(regex)) {
                // eslint-disable-next-line radix
                const thisSuffix = Number.parseInt(transformation.name.slice(prefix.length));
                if (thisSuffix > maxNumber) {
                    maxNumber = thisSuffix;
                }
            }
        }

        return prefix.concat((maxNumber + 1).toString());
    }

    public isUniqueName(name: string, renamedTransformation?: Transformation): boolean {
        for (const transformation of this.transformations) {
            if (
                transformation.name &&
                transformation !== renamedTransformation &&
                transformation.name.toUpperCase() === name.toUpperCase()
            ) {
                return false;
            }
        }

        return true;
    }

    public useCdcConnector(): boolean {
        for (const source of this.sources) {
            if (isSapOdpSource(source) || isSapODataSource(source)) {
                if (isSapCDCEnabled(source)) {
                    return true;
                }
            } else if (
                isTruthy(source.enableCdc) ||
                isTruthy(source.enableChangeFeed) ||
                isTruthy(source.enableNativeCdc)
            ) {
                return true;
            }
        }
        return false;
    }

    public getNumberOfSourcesWithStaging(): number {
        return this.sources.filter((source) => {
            return (
                source.sourceDataset instanceof SapOdpDataset ||
                source.datasetSettings instanceof SapOdpSettings ||
                (source.staged && source.sourceDataset instanceof AzureSqlDWTableDataset) ||
                ((source.datasetSettings instanceof SynapseAnalyticsSettings ||
                    source.datasetSettings instanceof HiveSettings) &&
                    source.datasetSettings.staged)
            );
        }).size;
    }

    public useSourceStagingConcurrencySettings(): boolean {
        return this.getNumberOfSourcesWithStaging() > 1;
    }

    public getDependencies(entity: Transformation): Transformation[] {
        const dependencies: Transformation[] = [];
        entity.inputStreams.forEach((inputStream) => {
            const dependency: Transformation = inputStream.owner as Transformation;
            dependencies.push(...this.getDependencies(dependency));
            dependencies.push(dependency);
        });
        return dependencies;
    }

    public getAllTransformations(): Transformation[] {
        const transformationList: Transformation[] = [];
        this.sources.forEach((source) => transformationList.push(source));
        this.sourceFlowlets.forEach((source) => transformationList.push(source));
        this.transformations.forEach((source) => transformationList.push(source));
        this.sinks.forEach((source) => transformationList.push(source));

        return transformationList;
    }

    public getParameterizedCallTransformations(): CallTransformation[] {
        const transformationList: CallTransformation[] = [];
        this.transformations.forEach((transformation) => {
            if (
                transformation instanceof CallTransformation &&
                transformation.source?.sourceLinkedService?.parameters?.size
            ) {
                transformationList.push(transformation);
            }
        });
        return transformationList;
    }

    public getAllOutputStreams(): DataFlowStream[] {
        const allOutputStreams: DataFlowStream[] = [];
        this.getAllTransformations().forEach((transformation) =>
            transformation.outputStreams.forEach((stream) => allOutputStreams.push(stream))
        );

        return allOutputStreams;
    }

    public getAllExpressions(): DataFlowExpression[] {
        const expressionList: DataFlowExpression[] = [];
        this.getAllExpressionHelper(this, expressionList, []);
        return expressionList;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public getAllExpressionHelper(obj: any, expressionList: DataFlowExpression[], visited: any[]): void {
        if (!obj || visited.includes(obj)) {
            return;
        }
        visited.push(obj);
        Object.keys(obj).forEach((key) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const attribute = (obj as any)[key];
            if (attribute instanceof DataFlowExpression) {
                expressionList.push(attribute);
            }
            if (Symbol.iterator in Object(attribute)) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                Array.from(attribute).forEach((item: any) => {
                    this.getAllExpressionHelper(item, expressionList, visited);
                });
            } else {
                this.getAllExpressionHelper(attribute, expressionList, visited);
            }
        });
    }

    public createTemplatableExpression(
        expectedType: string = StringDataFlowType.TYPE,
        defaultValue?: string
    ): DataFlowExpression {
        const expression = Owned.create<DataFlowExpression>(DataFlowExpression.MODEL, this);
        expression.requiredReturnType = expectedType;
        expression.expression = defaultValue;
        expression.isTemplatable = true;
        return expression;
    }

    public findTransformationByName(name: string): Transformation {
        const allTransformations = this.getAllTransformations();
        return allTransformations.find((transformation) => transformation.name === name);
    }

    /**
     * If removing transformation/sink, we just 1. simply remove the single piece 2. connect the input stream and output stream of deleted transfomation if exists.
     * If removing source, we remove the source but retain link information
     */
    public removeTransformation(transformation: Transformation): void {
        transformation.preRemoveAction();
        const firstInputStream = transformation.getFirstInputStream();
        const firstOutputStream = transformation.getFirstOutputStream();
        const modelName = Metamodel.modelOf(transformation).name;
        // when deleted transformation does not have input but has output
        if (!firstInputStream && firstOutputStream) {
            transformation.outputStreams.forEach((outputStream) => {
                for (let i = outputStream.consumers.length - 1; i >= 0; i--) {
                    const consumer = outputStream.consumers[i];
                    consumer.detachInputStream(outputStream);
                }
            });
            if (modelName === Metamodel.modelFor(DataFlowSource).name) {
                this.sources.delete(transformation as DataFlowSource);
                return;
            }
        }
        transformation.inputStreams.forEach((inputStream) => {
            transformation.detachInputStream(inputStream);
        });
        // when deleted transformation has both input and output.
        if (firstInputStream && firstOutputStream && firstOutputStream.consumers.length > 0) {
            transformation.outputStreams.forEach((outputStream) => {
                const consumersToResolve: Transformation[] = outputStream.consumers.slice();
                consumersToResolve.forEach((consumer) => {
                    consumer.insertInputStream(firstInputStream, consumer.detachInputStream(outputStream));
                });
            });
        }
        transformation.removeAllOutputStreams();
        switch (Metamodel.modelOf(transformation).name) {
            case Metamodel.modelFor(DataFlowSink).name:
                this.sinks.delete(transformation as DataFlowSink);
                this.rearrangeSinkOrder(transformation as DataFlowSink);
                break;
            case Metamodel.modelFor(Group).name:
                this.groups.delete(transformation as Group);
                break;
            case DataFlow._EXECUTE_FLOWLET_MODEL_NAME:
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                if ((transformation as any).isSource) {
                    this.sourceFlowlets.delete(transformation);
                } else {
                    this.transformations.delete(transformation);
                }
                break;
            default:
                this.transformations.delete(transformation);
        }
        this.forceNewDebugJobVersion();
    }

    public validateUniqueNames(): IViolation[] | undefined {
        const violations: IViolation[] = [];
        const transformationUpperNameSet = new Set<string>();
        const transformationUpperNameErrorSet = new Set<string>();

        this.getAllTransformations().forEach((transformation) => {
            if (transformation.name && !transformationUpperNameErrorSet.has(transformation.name.toUpperCase())) {
                if (transformationUpperNameSet.has(transformation.name.toUpperCase())) {
                    violations.push({
                        context: this,
                        message: () =>
                            sanitizeAndFormatString(
                                stringResources.DataFlowErrorDuplicatedName,
                                transformation.name.toUpperCase()
                            )
                    });
                    transformationUpperNameErrorSet.add(transformation.name.toUpperCase());
                } else {
                    transformationUpperNameSet.add(transformation.name.toUpperCase());
                }
            }
        });

        return violations;
    }

    public validateAlphanumericNames(): IViolation[] | undefined {
        const violations: IViolation[] = [];

        this.getAllTransformations().forEach((transformation: Transformation) => {
            const invalidNameViolations = Transformation.validateNameAlphanumeric(transformation);
            if (invalidNameViolations) {
                violations.push(...invalidNameViolations);
            }
        });

        return violations;
    }

    public validateInputs(): IViolation[] | undefined {
        const violations: IViolation[] = [];

        this.getAllTransformations().forEach((transformation: Transformation) => {
            if (!this.allSources().includes(transformation) && !transformation.inputStreams.length) {
                violations.push({
                    context: this,
                    message: () =>
                        sanitizeAndFormatString(
                            stringResources.DataFlowErrorMissingInput,
                            transformation.name.toUpperCase()
                        )
                });
            }
        });

        return violations;
    }

    public async validate(owner: unknown, context: IValidationContext): Promise<IViolation[] | undefined> {
        const violations: IViolation[] = [];

        if (!this.cacheUpdated) {
            await this.setupCache(context.contextData);
        }

        if (!this.hasSources()) {
            violations.push({
                context: this,
                message: () => sanitizeAndFormatString(stringResources.DataFlowErrorNoSource, this.name)
            });
        }
        if (!this.hasSinks()) {
            violations.push({
                context: this,
                message: () => sanitizeAndFormatString(stringResources.DataFlowErrorNoSink, this.name)
            });
        }

        violations.push(...this.validateUniqueNames(), ...this.validateInputs());
        return violations;
    }

    public gitValidate(dataFlow: DataFlow): IOwnedViolation[] | undefined {
        const violations: IOwnedViolation[] = [];
        const nameViolations = [...dataFlow.validateUniqueNames(), ...dataFlow.validateAlphanumericNames()];
        nameViolations.forEach((violation) => {
            violations.push({
                owned: dataFlow,
                context: violation.context,
                message: violation.message,
                corrected$: of(null),
                attribute: null
            });
        });
        return violations;
    }

    public async setupCache(contextData: IResourceContext): Promise<void> {
        const loggerService = await getServiceInstance<LoggerService>(contextData, 'LoggerService');
        await Promise.allSettled(
            compact([
                ...Array.from(this.transformations).map((transformation) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (transformation as any).flowlet?.setupCache(contextData)
                ),
                ...Array.from(this.sourceFlowlets).map((transformation) =>
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (transformation as any).flowlet?.setupCache(contextData)
                )
            ])
        ).then((results) => {
            results.forEach((result) => {
                if (result.status === 'rejected') {
                    loggerService.error(Metamodel.modelOf(this).name, 'setup cache error', {
                        name: this.name,
                        result: result.reason
                    });
                }
            });
        });
        if (!this.hasParsed) {
            await this._parseDSLTransformations(contextData);
        }
        this.updateCachedDataForTransformations();

        // Some expressions, such as cache sink expressions, cannot be evaluated until a second time this function is called
        this.updateCachedDataForTransformations();

        this.transformations.forEach((transformation) => {
            transformation.updateColumnReferences();
        });
        this.sinks.forEach((sink) => {
            sink.updateColumnReferences();
        });
    }

    public async tearDown(context: { inlineDataset?: boolean }): Promise<void> {
        await this.tearDownTransformations(context);
        Shadow.cast(context, this);
    }

    public async tearDownTransformations(context: { inlineDataset?: boolean }): Promise<void> {
        this.hasParsed = false;
        this.cacheUpdated = false;
        const newTransformations: Transformation[] = [];
        this.getAllTransformations().forEach((transformation) => {
            if (
                Metamodel.modelOf(transformation) === Metamodel.modelByName(DataFlow._EXECUTE_FLOWLET_MODEL_NAME) ||
                Metamodel.modelOf(transformation) === Metamodel.modelByName(DataFlow._CALL_TRANSFORM_MODEL_NAME)
            ) {
                const transform = Copier.clone(context, [transformation], undefined, false)[0] as Transformation;
                transform.removeAllInputStreams();

                newTransformations.push(transform);
            }
            transformation.clearCache();
        });
        this.transformations.clear();
        this.sourceFlowlets.clear();

        const newSinks: DataFlowSink[] = [];
        this.sinks.forEach((sink) => {
            sink.removeAllInputStreams();
            const newSink = Metamodel.modelFor(DataFlowSink).create<DataFlowSink>(sink.name);
            newSink.sinkDataset = sink.sinkDataset;
            newSink.sinkLinkedService = sink.sinkLinkedService;
            newSink.schemaLinkedService = sink.schemaLinkedService;
            newSink.description = sink.description;
            newSink.snowflakeStageInsert = sink.snowflakeStageInsert;
            newSink.useCopyCommandForStaging = sink.useCopyCommandForStaging;
            newSink.restDataFormat = sink.restDataFormat;
            assignDataFlowExpressionValue(newSink.insertHttpMethod, sink.insertHttpMethod);
            assignDataFlowExpressionValue(newSink.upsertHttpMethod, sink.upsertHttpMethod);
            assignDataFlowExpressionValue(newSink.updateHttpMethod, sink.updateHttpMethod);
            assignDataFlowExpressionValue(newSink.deleteHttpMethod, sink.deleteHttpMethod);
            newSinks.push(newSink);
        });
        this.sinks.clear();

        const newSources: DataFlowSource[] = [];
        this.sources.forEach((source) => {
            source.removeAllOutputStreams();
            const newSource = Metamodel.modelFor(DataFlowSource).create<DataFlowSource>(source.name);
            newSource.sourceDataset = source.sourceDataset;
            newSource.sourceLinkedService = source.sourceLinkedService;
            newSource.schemaLinkedService = source.schemaLinkedService;
            newSource.description = source.description;
            newSources.push(newSource);
        });
        this.sources.clear();

        newSources.forEach((ns) => this.addSource(ns));
        newSinks.forEach((ns) => this.addSink(ns));
        newTransformations.forEach((nt) => {
            if (
                Metamodel.modelOf(nt) === Metamodel.modelByName(DataFlow._EXECUTE_FLOWLET_MODEL_NAME) &&
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (nt as any).isSource
            ) {
                this.addSourceFlowlet(nt);
            } else {
                this.addTransformation(nt);
            }
        });
    }

    public updateCachedDataForTransformations(inputTransformations?: Transformation[]): void {
        let transformations: Transformation[] = [];
        if (inputTransformations) {
            inputTransformations.forEach((transformation) => {
                transformations.push(transformation);
                transformations = transformation.getAllDownTransformations(transformations);
            });
        } else {
            this.allSources().forEach((source) => {
                transformations.push(source);
                transformations = source.getAllDownTransformations(transformations);
            });
        }

        this.updateCachedDataForSelectedTransformations(transformations);
        this.cacheUpdated = true;
    }

    public updateCachedDataForSelectedTransformations(transformations: Transformation[]): void {
        const visited = new Map<Transformation, boolean>();
        const stack: Transformation[] = [];

        transformations.forEach((transform) => {
            if (!visited.get(transform)) {
                DataFlowUtils.topologicalSort(transform, stack, visited);
            }
        });

        if (GlobalVariables.validateDataFlowAPI && this.containsUnknownDSL) {
            // eslint-disable-next-line no-restricted-syntax
            const schemaResponse = Application.injector.get(DataFlowDataPlaneService).validateDataFlow(this, true);
            schemaResponse.then((schemaResponseVal) => {
                while (stack.length > 0) {
                    const transform = stack.pop();
                    const schemaAPIValue = schemaResponseVal.streams[transform.name];
                    if (schemaAPIValue) {
                        transform.updateTransformationCache(schemaAPIValue);
                    } else {
                        transform.updateTransformationCache();
                    }
                }
            });
        } else {
            while (stack.length > 0) {
                const transform = stack.pop();
                transform.updateTransformationCache();
            }
        }
    }

    public forceNewDebugJobVersion(): void {
        this.forceToGetNewJobVersion = true;
        this.changesMadeSinceLastJob = true;

        // eslint-disable-next-line no-restricted-syntax
        const msgSvc = Application.injector.get(MessagingService);
        msgSvc.broadcast(
            MessagingServiceChannel.DataFlowNewDebugJobRequired,
            new Message(MessageKind.Entity, { entity: this, contextData: msgSvc.contextData })
        );
    }

    public setLastStoredResource(dataFlow: IDataFlow): void {
        const props = (dataFlow.properties as IDataFlowProperties).typeProperties as IDataFlowPreviewProperties;
        if (!props) {
            return;
        }
        this._lastStoredResource = props;
    }

    // why were there so many `parser parses w/ this, parser parses w/ that, parser parses w/ wtv` again and over again
    // however, none actually does parse, terrific
    public async parseDslAsync(
        adapterContext: AdapterContext,
        resource?: IDataFlow,
        option: Partial<IDataFlowModelParseDslAsyncOption> = {}
    ): Promise<void> {
        const method: ParseScriptMethod = option.method ?? ParseScriptMethod.ALL;
        const parserVersion: IDataFlowModelParseDslAsyncOption['parserVersion'] = option.parserVersion ?? 'vStable';
        const dataflowParser = this.getParser();
        const props =
            (resource && resource.properties && ((resource.properties as IDataFlowProperties).typeProperties as IDataFlowPreviewProperties)) ||
            this._lastStoredResource;
        const fastQueue = !!adapterContext.adapterConfig.fastQueue;

        if (!props) {
            return;
        }

        if (props.script && !props.scriptLines) {
            this.savedWithSingleScript = true;
        }
        if (props.scriptLines && !props.script) {
            props.script = props.scriptLines
                .map((line) => line.replace(/^(     )+/, (result) => '\t'.repeat(result.length / 5)))
                .join('\n');
        }

        if (props.udfLibraries) {
            for (const udfLibrary of props.udfLibraries) {
                const udfLibraryRef = ReferenceAdapter.toProxyModel<DataFlow>(DataFlow.MODEL, udfLibrary, adapterContext);
                udfLibraryRef.name = udfLibrary.referenceName;
                this.addUdfLibrary(udfLibraryRef);
            }
        }

        this._lastStoredResource = props;
        // props.script = 'functions{\n\tabbreviate(string) as string = shorten(i1, 2),\n'
        //     + 'logarithm(integer) as double \'com.microsoft.dataflow.javaExtension.ExtensionFunctions.logarithm\'\n}\n'
        //     + 'parameters{\n\tabc as string constant\n,test as string expression}\n';
        const loggerService = await getServiceInstance<LoggerService>(adapterContext.contextData, 'LoggerService');
        const startTime = performance.now();
        if (props.script) {
            const scriptProps = await dataflowParser.parseDataFlowScriptAsync(
                adapterContext.contextData,
                props.script,
                this,
                {
                    method,
                    fastQueue,
                    parserVersion
                }
            );
            if (method === ParseScriptMethod.ALL || method === ParseScriptMethod.PARAMETERS) {
                this.addFunctions(scriptProps.functions);
                this.addParameters(scriptProps.parameters);
                this.parameters.forEach((param) => {
                    // setting identity in here since dataflow parameters come from dsl
                    const parameterIdentity = AdapterContext.getIdentity(
                        generateIdentity(DataFlow.MODEL.name, this.name),
                        param.name
                    );
                    setIdentity(param, parameterIdentity);
                });

                // only execute flowlet transformation and call transformation will be here
                for (const transformationModel of scriptProps.transformations) {
                    const adapterModel = adapterContext.asyncAdapterFor(Metamodel.modelOf(transformationModel));
                    if (props.transformations) {
                        const transformationResource = props.transformations.find(
                            (transformation) => transformation.name === transformationModel.name
                        );
                        if (transformationResource) {
                            await adapterModel.toModel(transformationResource, transformationModel);
                        }
                    }
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    if ((transformationModel as any).isSource) {
                        this.addSourceFlowlet(transformationModel);
                    } else {
                        this.addTransformation(transformationModel);
                    }
                }
            }

            if (method === ParseScriptMethod.ALL || method === ParseScriptMethod.TRANSFORMATIONS) {
                for (const transformationModel of scriptProps.transformations) {
                    if (!this.startStopTransformation(transformationModel)) {
                        // handle transformation description
                        const adapterModel = adapterContext.asyncAdapterFor(Metamodel.modelOf(transformationModel));
                        if (props.transformations) {
                            const transformationResource = props.transformations.find(
                                (transformation) => transformation.name === transformationModel.name
                            );
                            if (transformationResource) {
                                await adapterModel.toModel(transformationResource, transformationModel);
                            }
                        }
                        this.addTransformation(transformationModel);
                    }
                }
                // Because we are parsing the DSL, wait until all sources, transformations and sinks are populated before populating the parameters default return value type.
                const dataFlowArguments = getDataFlowBaseArguments(this);
                this.parameters.forEach((param) => {
                    getExpressionReturnTypeAndUpdateIdentifiers(
                        param.defaultValueExpression.expression,
                        dataFlowArguments,
                        param.defaultValueExpression
                    );
                });
            }
            // handle transformations when we unify in 1 single script
        } else {
            if (props.sources) {
                for (const source of props.sources) {
                    await dataflowParser.parseDataFlowScriptAsync(adapterContext.contextData, source.script, this, {
                        fastQueue,
                        method
                    });
                }
            }
            if (props.sinks) {
                for (const sink of props.sinks) {
                    await dataflowParser.parseDataFlowScriptAsync(adapterContext.contextData, sink.script, this, {
                        fastQueue,
                        method
                    });
                }
            }
            if (props.transformations) {
                for (const transformationResource of props.transformations) {
                    const transforms = await dataflowParser.parseDataFlowScriptAsync(
                        adapterContext.contextData,
                        transformationResource.script,
                        this,
                        {
                            fastQueue,
                            method
                        }
                    );
                    for (const transformation of transforms.transformations) {
                        const adapterModel = adapterContext.asyncAdapterFor(Metamodel.modelOf(transformation));
                        await adapterModel.toModel(transformationResource, transformation);
                        this.addTransformation(transformation);
                    }
                }
            }
        }
        let allTransformations = [];
        if (method === ParseScriptMethod.ALL || method === ParseScriptMethod.TRANSFORMATIONS) {
            this.addInputStreams();

            allTransformations = this.getAllTransformations();
            allTransformations.forEach((transformation) => transformation.clearCache());
            this.cacheUpdated = false;
        }
        loggerService.logEvent(
            new PerformanceEvent(
                Metamodel.modelOf(this).name,
                loggerConstants.Operation_DataFlowParseDSLAsync,
                performance.now() - startTime,
                allTransformations.length.toString()
            )
        );
    }

    public checkMissingParameterValues(
        dataflowParameterValues: ParameterValue[] = [],
        datasetParameterValues: Dictionary<ParameterValue[]> = {}
    ): void {
        const validateParam = (parameterValue: ParameterValue, parameter: Parameter) =>
            parameterValue.parameter === parameter && !!parameterValue.value;
        for (const parameter of this.parameters) {
            if (
                !parameter.defaultValue &&
                !dataflowParameterValues.find((parameterValue) => validateParam(parameterValue, parameter))
            ) {
                this.parametersMissingValues = true;
                return;
            }
        }

        for (const source of [].concat(Array.from(this.sources), Array.from(this.sinks))) {
            if (source && source.parameters) {
                const sourceParameterValues = datasetParameterValues[source.name] || [];
                for (const datasetParameter of source.parameters) {
                    if (
                        !datasetParameter.defaultValue &&
                        !sourceParameterValues.find((parameterValue) => validateParam(parameterValue, datasetParameter))
                    ) {
                        this.parametersMissingValues = true;
                        return;
                    }
                }
            }
        }

        this.parametersMissingValues = false;
    }

    public rearrangeSinkOrder(deletedSink: DataFlowSink): void {
        if (!deletedSink.saveOrder.templateExpression) {
            const deletedSinkOrder = +deletedSink.saveOrder.expression;
            let maxOrderDecreased = false;
            Array.from(this.sinks).forEach((sink) => {
                if (!sink.saveOrder.templateExpression && +sink.saveOrder.expression > deletedSinkOrder) {
                    sink.saveOrder.expression = (+sink.saveOrder.expression - 1).toString();
                    maxOrderDecreased = true;
                }
            });
            if (maxOrderDecreased) {
                this.maxSinkOrder--;
            }
        }
    }

    public initSinkGroups(): void {
        const sinkGroups: ISinkGroup[] = [];
        const visited: Set<Transformation> = new Set<Transformation>();
        if (this.sinks) {
            let groupIndex = 1;
            this.sinks.forEach((sink) => {
                if (!visited.has(sink)) {
                    const sinkGroup: Transformation[] = [sink];
                    visited.add(sink);

                    // For each upstream source connected to a sink, find all downstream sinks for that sink
                    const upstreamSources = sink
                        .getAllUpTransformations()
                        .filter((transformation) => transformation instanceof DataFlowSource);
                    upstreamSources.forEach((upstreamSource) => {
                        const downstreamSinks = upstreamSource
                            .getAllDownTransformations()
                            .filter((transformation) => transformation instanceof DataFlowSink);
                        downstreamSinks.forEach((downstreamSink) => {
                            if (!visited.has(downstreamSink)) {
                                sinkGroup.push(downstreamSink);
                                visited.add(downstreamSink);
                            }
                        });
                    });

                    const sinkGroupSorted = sinkGroup.sort((a, b) => {
                        return DataFlowUtils.sinkCompare(a as DataFlowSink, b as DataFlowSink);
                    });

                    sinkGroups.push({
                        groupName: `sinkGroup`,
                        groupId: groupIndex,
                        sinks: sinkGroupSorted
                    });
                    groupIndex++;
                }
            });
        }
        this.sinkGroups = sinkGroups;
    }

    public getUserDefinedFunctionsUsed(): Set<string> {
        const usedUserDefinedFunctions = new Set<string>();
        let transformations: Transformation[] = [];
        this.allSources().forEach((source) => {
            transformations.push(source);
            transformations = source.getAllDownTransformations(transformations);
        });
        transformations.forEach((transformation) => {
            transformation.getExpressions().forEach((expression) => {
                if (expression.hasUserDefinedFunction) {
                    expression.userDefinedFunctionsUsed.forEach((func) => usedUserDefinedFunctions.add(func));
                }
            });
        });
        return usedUserDefinedFunctions;
    }

    public getCallTransformations(): DataFlowSource[] {
        const sourceList: DataFlowSource[] = [];
        this.getAllTransformations().forEach((transformation) => {
            if (transformation instanceof CallTransformation) {
                sourceList.push(transformation.source as DataFlowSource);
            }
        });
        return sourceList;
    }

    public getSourceTransformations(): DataFlowSource[] {
        const sourceList: DataFlowSource[] = [];
        this.getAllTransformations().forEach((transformation) => {
            if (
                Metamodel.modelOf(transformation).name === DataFlow._EXECUTE_FLOWLET_MODEL_NAME &&
                (transformation as ExecuteFlowletTransformation).flowlet
            ) {
                sourceList.push(...(transformation as ExecuteFlowletTransformation).flowlet.getSourceTransformations());
            }

            if (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSource) {
                sourceList.push(transformation as DataFlowSource);
            }
        });
        return sourceList;
    }

    public getSinkTransformations(): DataFlowSink[] {
        const sinkList: DataFlowSink[] = [];
        this.getAllTransformations().forEach((transformation) => {
            if (
                Metamodel.modelOf(transformation).name === DataFlow._EXECUTE_FLOWLET_MODEL_NAME &&
                (transformation as ExecuteFlowletTransformation).flowlet
            ) {
                sinkList.push(...(transformation as ExecuteFlowletTransformation).flowlet.getSinkTransformations());
            }

            if (Metamodel.modelOf(transformation).name === ModelConstants.dataFlowSink) {
                sinkList.push(transformation as DataFlowSink);
            }
        });
        return sinkList;
    }

    protected getParser(): DataFlowParser {
        return new DataFlowParser();
    }

    protected startStopTransformation(transformationModel: Transformation): boolean {
        return transformationModel instanceof DataFlowSource || transformationModel instanceof DataFlowSink;
    }

    protected hasSources(): boolean {
        return this.allSources().length > 0;
    }

    protected hasSinks(): boolean {
        return this.sinks.size !== 0;
    }

    protected hasUdfLibraries(): boolean {
        return this.udfLibraries.size !== 0;
    }

    protected allSources(): Transformation[] {
        return [...this.sources, ...this.sourceFlowlets];
    }

    protected addInputStreams(): void {
        this.sinks.forEach((sink) => {
            this._addInputStream(sink);
        });
        this.transformations.forEach((transformation) => {
            this._addInputStream(transformation);
        });
    }

    protected _addInputStream(transformation: Transformation): void {
        transformation.inputStreamNames.forEach((inputStreamName) => {
            // For now doing case sensitive comparison, not sure what our grammer permits.
            const streamModel = this.getAllOutputStreams().find((stream) => stream.qualifiedName === inputStreamName);
            if (streamModel) {
                transformation.attachInputStream(streamModel);
            }
        });
    }

    protected async _parseDSLTransformations(contextData: IResourceContext): Promise<void> {
        this.hasParsed = true;
        await this.parseDslAsync(AdapterContext.defaultContextOf(contextData), undefined, {
            method: ParseScriptMethod.TRANSFORMATIONS
        });
        const msgSvc = await getServiceInstance<MessagingService>(contextData, 'MessagingService');
        msgSvc.broadcast(MessagingServiceChannel.DataFlowUpdateCanvas, new Message(MessageKind.Entity, this));
    }
}
